import { parseMidi, writeMidi, type MidiEvent } from 'midi-file'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type DroppedTrack = { index: number; reason: string }
export type SimplifySummary = {
  nIn: number
  nOut: number
  dropped: DroppedTrack[]   // sorted by original index
  melody: string            // e.g. "T1 (Violin)" or "none"
}
export type SimplifyResult = {
  bytes: Uint8Array          // re-encoded simplified .mid
  summary: SimplifySummary
}

// ---------------------------------------------------------------------------
// Regexes (mirror simplify_midi.py)
// ---------------------------------------------------------------------------

const MELODY_NAME_RE = /melody|lead|vocal|soprano|tune/i
const SIDE_NAME_RE = /\b(right|left|r\.?h|l\.?h)\b/i
const DRUM_NAME_RE =
  /\bdrum|\bpercussion|\bperc\b|\bkick\b|\bsnare\b|\bcymbal|\bhi-?hat\b|\bhat\b|\bshaker\b|\bclap\b|\bclaves\b|\btom\b|\btambourine/i

const DRUM_CHANNEL = 9

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

// (absoluteTick, note, velocity, channel)
type NoteEvent = [number, number, number, number]

interface TrackInfo {
  index: number
  name: string
  events: NoteEvent[]
  nNotes: number
  isDrum: boolean
  fingerprint: Array<[number, number]>
  meanPitch: number
  mono: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function trackName(track: MidiEvent[]): string {
  for (const msg of track) {
    if (msg.type === 'trackName') {
      return (msg as { type: 'trackName'; text: string }).text
    }
  }
  return ''
}

function noteEvents(track: MidiEvent[]): NoteEvent[] {
  let t = 0
  const out: NoteEvent[] = []
  for (const msg of track) {
    t += msg.deltaTime
    if (msg.type === 'noteOn') {
      const e = msg as { type: 'noteOn'; noteNumber: number; velocity: number; channel: number }
      if (e.velocity > 0) {
        out.push([t, e.noteNumber, e.velocity, e.channel])
      } else {
        // noteOn with velocity 0 is treated as noteOff
        out.push([t, e.noteNumber, 0, e.channel])
      }
    } else if (msg.type === 'noteOff') {
      const e = msg as { type: 'noteOff'; noteNumber: number; velocity: number; channel: number }
      out.push([t, e.noteNumber, 0, e.channel])
    }
  }
  return out
}

function isDrumTrack(events: NoteEvent[], name: string): boolean {
  if (name && DRUM_NAME_RE.test(name)) {
    return true
  }
  if (events.length === 0) {
    return false
  }
  const drumCount = events.filter(([, , , ch]) => ch === DRUM_CHANNEL).length
  return drumCount / events.length > 0.5
}

function makeFingerprint(events: NoteEvent[]): Array<[number, number]> {
  // Channel- and velocity-agnostic. Only note-on events (velocity > 0).
  return events.filter(([, , v]) => v > 0).map(([t, n]) => [t, n] as [number, number])
}

function nearDuplicate(fpA: Array<[number, number]>, fpB: Array<[number, number]>, tolerance = 0.05): boolean {
  if (fpA.length === 0 || fpB.length === 0) return false
  // Check exact equality first
  if (fpA.length === fpB.length && fpA.every(([t, n], i) => t === fpB[i][0] && n === fpB[i][1])) {
    return true
  }
  // Jaccard-style diff/union using Set of stringified pairs
  const strA = new Set(fpA.map(([t, n]) => `${t},${n}`))
  const strB = new Set(fpB.map(([t, n]) => `${t},${n}`))
  const union = new Set([...strA, ...strB])
  let diff = 0
  for (const k of union) {
    if (!strA.has(k) || !strB.has(k)) diff++
  }
  return union.size > 0 && diff / union.size < tolerance
}

function baseName(name: string): string {
  return name.replace(SIDE_NAME_RE, '').replace(/^[ \-_]+|[ \-_]+$/g, '').toLowerCase()
}

function stereoPair(nameA: string, nameB: string): boolean {
  if (!nameA || !nameB) return false
  if (!(SIDE_NAME_RE.test(nameA) && SIDE_NAME_RE.test(nameB))) return false
  return baseName(nameA) === baseName(nameB)
}

function monophonyScore(events: NoteEvent[]): number {
  const notesOn = events.filter(([, , v]) => v > 0)
  if (notesOn.length < 2) return 1.0

  // Stable sort by absoluteTick (JS Array.sort is stable since TC39 2019)
  const timeline = events.slice().sort((a, b) => a[0] - b[0])

  let active = 0
  let overlaps = 0
  let onsets = 0

  for (const [, , v] of timeline) {
    if (v > 0) {
      onsets++
      if (active > 0) overlaps++
      active++
    } else {
      active = Math.max(0, active - 1)
    }
  }
  return overlaps / Math.max(onsets, 1)
}

function meanPitchOf(events: NoteEvent[]): number {
  const pitches = events.filter(([, , v]) => v > 0).map(([, n]) => n)
  if (pitches.length === 0) return 0.0
  return pitches.reduce((a, b) => a + b, 0) / pitches.length
}

// Population standard deviation (divisor N, not N-1) — mirrors Python statistics.pstdev
function populationStdDev(values: number[]): number {
  if (values.length === 0) return 0
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length
  return Math.sqrt(variance)
}

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

function classifyTracks(tracks: MidiEvent[][]): TrackInfo[] {
  return tracks.map((track, idx) => {
    const events = noteEvents(track)
    const name = trackName(track)
    const fp = makeFingerprint(events)
    return {
      index: idx,
      name,
      events,
      nNotes: events.filter(([, , v]) => v > 0).length,
      isDrum: isDrumTrack(events, name),
      fingerprint: fp,
      meanPitch: meanPitchOf(events),
      mono: monophonyScore(events),
    }
  })
}

function findDupes(info: TrackInfo[], keepDropped: Array<[number, string]>): Set<number> {
  const seen: TrackInfo[] = []
  const drop = new Set<number>()

  for (const tr of info) {
    if (tr.index === 0 || tr.isDrum || tr.nNotes === 0) continue

    let matched: number | null = null
    for (const kept of seen) {
      if (nearDuplicate(tr.fingerprint, kept.fingerprint)) {
        matched = kept.index
        break
      }
      if (stereoPair(tr.name, kept.name) && Math.abs(tr.nNotes - kept.nNotes) <= 2) {
        matched = kept.index
        break
      }
    }

    if (matched !== null) {
      drop.add(tr.index)
      keepDropped.push([tr.index, `dup-of-T${matched}`])
    } else {
      seen.push(tr)
    }
  }
  return drop
}

function pickMelody(info: TrackInfo[], keptIndices: Set<number>): TrackInfo | null {
  const candidates = info.filter(
    (tr) => keptIndices.has(tr.index) && tr.index !== 0 && tr.nNotes > 0
  )
  if (candidates.length === 0) return null

  const pitches = candidates.map((c) => c.meanPitch)
  const pmean = pitches.reduce((a, b) => a + b, 0) / pitches.length
  let pstd = candidates.length > 1 ? populationStdDev(pitches) : 1.0
  if (pstd === 0) pstd = 1.0

  let best: TrackInfo | null = null
  let bestScore = 0.0

  for (const c of candidates) {
    let score = 0.0
    if (MELODY_NAME_RE.test(c.name || '')) score += 3
    if (c.mono <= 0.10) score += 2
    score += (c.meanPitch - pmean) / pstd
    if (c.meanPitch < 50) score -= 2
    if (score > bestScore) {
      bestScore = score
      best = c
    }
  }
  return best
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function simplifyMidi(input: Uint8Array): SimplifyResult {
  const midi = parseMidi(input)
  const info = classifyTracks(midi.tracks)
  const nIn = midi.tracks.length

  // --- Drop drums ---
  const droppedPairs: Array<[number, string]> = []
  const drumDrop = new Set<number>()
  for (const tr of info) {
    if (tr.index !== 0 && tr.isDrum) {
      drumDrop.add(tr.index)
      droppedPairs.push([tr.index, 'drum'])
    }
  }

  // --- Drop duplicates (only among non-drum survivors) ---
  const remainingInfo = info.filter((tr) => !drumDrop.has(tr.index))
  const dupDrop = findDupes(remainingInfo, droppedPairs)

  // --- Kept indices ---
  const kept = info
    .filter((tr) => !drumDrop.has(tr.index) && !dupDrop.has(tr.index))
    .map((tr) => tr.index)
  const keptSet = new Set(kept)

  // --- Pick melody ---
  const melody = pickMelody(info, keptSet)

  // --- Build output order: [0, melody?, ...rest in original order] ---
  const newOrder: number[] = [0]
  if (melody !== null && keptSet.has(melody.index) && melody.index !== 0) {
    newOrder.push(melody.index)
  }
  for (const idx of kept) {
    if (!newOrder.includes(idx)) {
      newOrder.push(idx)
    }
  }

  // --- Build output MidiData ---
  const outTracks = newOrder.map((idx) => midi.tracks[idx])
  const outMidi = {
    header: {
      format: midi.header.format,
      numTracks: newOrder.length,
      ticksPerBeat: midi.header.ticksPerBeat,
      timeDivision: midi.header.timeDivision,
      framesPerSecond: midi.header.framesPerSecond,
      ticksPerFrame: midi.header.ticksPerFrame,
    },
    tracks: outTracks,
  }

  // running: true tells midi-file's writer to emit MIDI running-status compression
  // (omit the status byte when consecutive channel-voice messages share the same status),
  // matching Python mido's default output behavior.
  const bytes = new Uint8Array(writeMidi(outMidi, { running: true }))

  // --- Build summary ---
  // Sort dropped by original index (mirror Python's sorted(dropped))
  const sortedDropped: DroppedTrack[] = droppedPairs
    .sort(([a], [b]) => a - b)
    .map(([index, reason]) => ({ index, reason }))

  const melodyLabel =
    melody !== null
      ? `T${melody.index} (${melody.name || 'unnamed'})`
      : 'none'

  const summary: SimplifySummary = {
    nIn,
    nOut: newOrder.length,
    dropped: sortedDropped,
    melody: melodyLabel,
  }

  return { bytes, summary }
}
